import React from "react";

import {
  MONTH_LABEL, UNSCHEDULED, capacityFill, capacityState, formatCell,
  formatHours,
} from "./cellFormat";

// Rows are employees with Unassigned pinned at the top -- that row is the
// work still to distribute, which is what a manager opens this page to do.
// Columns are months, with a row total on the right and a firm total along
// the bottom: "am I overloaded" and "is the firm overloaded" are different
// questions, and the old grid answered neither without mental arithmetic.
//
// A cell leads with its job count, which is always known, and says what it
// knows about hours underneath. Capacity colours; it never blocks.

const COLUMN_LABEL = (key) =>
  key === UNSCHEDULED ? "Unscheduled" : MONTH_LABEL(key);

// One palette for all four states, so a cell, a row total and a column total
// that mean the same thing look the same.
// Over capacity is the loudest thing on the page, on purpose. The old grid
// marked it with a tint pale enough that nobody noticed an overloaded month
// until it arrived.
const STATE_STYLE = {
  over: {
    background: "#FEE2E2", color: "#7F1D1D", muted: "#B91C1C", bar: "#DC2626",
  },
  near: {
    background: "#FFFBEB", color: "#92400E", muted: "#B45309", bar: "#F59E0B",
  },
  under: {
    background: "transparent", color: "#111827", muted: "#6B7280",
    bar: "#9CA3AF",
  },
  none: {
    background: "transparent", color: "#111827", muted: "#6B7280",
    bar: "#9CA3AF",
  },
};

function CapacityBar({ rowKey, month, fill, tone }) {
  return (
    <div
      data-testid={`bar-${rowKey}-${month}`}
      data-fill={String(fill)}
      style={{
        height: 4, marginTop: 4, borderRadius: 2, background: "#E5E7EB",
        overflow: "hidden",
      }}
    >
      <div style={{ width: `${fill}%`, height: "100%", background: tone.bar }} />
    </div>
  );
}

function Cell({ rowKey, month, cell, capacity, onCellClick }) {
  // Capacity is compared per month only; the Unscheduled column is a holding
  // area, not a period, so its caller passes null and this reads 'none'.
  const state = cell ? capacityState(cell.hours, capacity) : "none";
  const tone = STATE_STYLE[state];
  const { primary, secondary, unknownNote } = formatCell(cell);
  const fill = cell ? capacityFill(cell.hours, capacity) : null;
  // By how much, not just that -- "over" tells a manager to look, "10h over"
  // tells them whether it is worth moving something.
  const overBy =
    state === "over" ? parseFloat(cell.hours) - Number(capacity) : 0;

  return (
    <td
      style={{
        padding: 0,
        textAlign: "right",
        borderBottom: "1px solid #F3F4F6",
        whiteSpace: "nowrap",
      }}
    >
      {cell ? (
        <button
          type="button"
          data-testid={`cell-${rowKey}-${month}`}
          data-capacity-state={state}
          onClick={() => onCellClick(rowKey, month)}
          style={{
            width: "100%",
            padding: "6px 10px",
            textAlign: "right",
            cursor: "pointer",
            background: tone.background,
            color: tone.color,
            border: "none",
            borderLeft: state === "over" ? "4px solid #DC2626" : "none",
            font: "inherit",
            lineHeight: 1.3,
          }}
        >
          <div><strong>{primary}</strong></div>
          <div style={{ color: tone.muted, fontSize: 12 }}>{secondary}</div>
          {state === "over" ? (
            <div style={{ color: "#B91C1C", fontSize: 12, fontWeight: 700 }}>
              {`${formatHours(overBy)}h over`}
            </div>
          ) : null}
          {/* Was an asterisk explained in a title attribute nobody hovers. */}
          {unknownNote ? (
            <div style={{ color: tone.muted, fontSize: 11 }}>{unknownNote}</div>
          ) : null}
          {fill != null ? (
            <CapacityBar
              rowKey={rowKey} month={month} fill={fill} tone={tone}
            />
          ) : null}
        </button>
      ) : (
        <span
          data-testid={`cell-${rowKey}-${month}`}
          data-capacity-state="none"
          style={{ display: "inline-block", padding: "8px 10px" }}
        />
      )}
    </td>
  );
}

// Totals arrived with this release. A payload cached from before it must
// still render a grid rather than blanking the page, so every read of
// `totals` goes through these defaults.
const EMPTY_TOTALS = { by_column: {}, by_row: {}, capacity_by_column: {} };

function TotalText({ total }) {
  if (!total || !total.count) return null;
  const hours = parseFloat(total.hours);
  return (
    <>
      <strong>{`${total.count} job${total.count === 1 ? "" : "s"}`}</strong>
      {/* Zero hours against a real job count means "nothing estimated here",
          not "no work here" -- printing "0h" would say the second. */}
      {Number.isFinite(hours) && hours > 0 ? (
        <div style={{ fontSize: 12, color: "#6B7280" }}>
          {`${formatHours(total.hours)}h`}
        </div>
      ) : null}
    </>
  );
}

export default function CapacityView({ grid, onCellClick }) {
  const { months, employees, cells } = grid;
  // The backend sends an explicit render order with Unscheduled leading. Fall
  // back to months alone so a stale cached payload still renders rather than
  // blanking the page.
  const columns = grid.columns || months;
  const totals = grid.totals || EMPTY_TOTALS;
  const hasTotals = Boolean(grid.totals);
  const rows = [
    { key: "unassigned", name: "Unassigned", capacity: null },
    ...employees.map((e) => ({
      key: String(e.id),
      name: e.name,
      capacity: e.monthly_capacity_hours,
    })),
  ];

  const headCellStyle = {
    textAlign: "right", padding: "8px 10px", color: "#6B7280",
  };

  return (
    <div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 640 }}>
          <thead>
            <tr>
              <th style={{ textAlign: "left", padding: "8px 10px", color: "#6B7280" }}>
                Employee
              </th>
              {columns.map((m) => (
                <th
                  key={m}
                  style={{
                    ...headCellStyle,
                    // Separated from the months so it reads as a holding area
                    // rather than a period on the calendar.
                    ...(m === UNSCHEDULED
                      ? { borderRight: "2px solid #E5E7EB", color: "#B45309" }
                      : null),
                  }}
                >
                  {COLUMN_LABEL(m)}
                </th>
              ))}
              {hasTotals ? (
                <th style={{ ...headCellStyle, borderLeft: "2px solid #E5E7EB" }}>
                  Total
                </th>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key}>
                <td
                  style={{
                    padding: "8px 10px",
                    fontWeight: row.key === "unassigned" ? 600 : 400,
                    borderBottom: "1px solid #F3F4F6",
                    whiteSpace: "nowrap",
                  }}
                >
                  {row.name}
                  {row.capacity != null ? (
                    <span style={{ color: "#9CA3AF", marginLeft: 6, fontSize: 12 }}>
                      {row.capacity}h/mo
                    </span>
                  ) : null}
                </td>
                {columns.map((m) => (
                  <Cell
                    key={m}
                    rowKey={row.key}
                    month={m}
                    cell={(cells[row.key] || {})[m]}
                    capacity={m === UNSCHEDULED ? null : row.capacity}
                    onCellClick={onCellClick}
                  />
                ))}
                {hasTotals ? (
                  <td
                    data-testid={`total-row-${row.key}`}
                    style={{
                      padding: "8px 10px",
                      textAlign: "right",
                      whiteSpace: "nowrap",
                      borderLeft: "2px solid #E5E7EB",
                      borderBottom: "1px solid #F3F4F6",
                    }}
                  >
                    <TotalText total={totals.by_row[row.key]} />
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
          {hasTotals ? (
            <tfoot>
              <tr style={{ borderTop: "2px solid #E5E7EB" }}>
                <td style={{ padding: "8px 10px", fontWeight: 600 }}>Firm total</td>
                {columns.map((m) => {
                  const total = totals.by_column[m];
                  const capacity = totals.capacity_by_column[m];
                  const state = total
                    ? capacityState(total.hours, capacity ?? null)
                    : "none";
                  return (
                    <td
                      key={m}
                      data-testid={`total-column-${m}`}
                      style={{
                        padding: "8px 10px",
                        textAlign: "right",
                        whiteSpace: "nowrap",
                        color: STATE_STYLE[state].color,
                        background: STATE_STYLE[state].background,
                      }}
                    >
                      <TotalText total={total} />
                      {/* Capacity only on the months. The Unscheduled column
                          has no month to be a capacity for, and printing one
                          would invite a comparison that means nothing. */}
                      {capacity != null ? (
                        <div style={{ fontSize: 12, color: STATE_STYLE[state].muted }}>
                          {`of ${formatHours(capacity)}h`}
                        </div>
                      ) : null}
                    </td>
                  );
                })}
                <td style={{ borderLeft: "2px solid #E5E7EB" }} />
              </tr>
            </tfoot>
          ) : null}
        </table>
      </div>

      {/* Unknowns are counted but excluded from the hour totals, so they are
          stated out loud rather than left to make the numbers quietly wrong. */}
      {grid.unknown_estimate_count > 0 ? (
        <p style={{ color: "#6B7280", fontSize: 13, marginTop: 10 }}>
          {grid.unknown_estimate_count} task
          {grid.unknown_estimate_count === 1 ? "" : "s"} with no estimate —
          counted above, but not included in the hours.
        </p>
      ) : null}
      {grid.undated_count > 0 ? (
        <p style={{ color: "#6B7280", fontSize: 13, marginTop: 4 }}>
          {grid.undated_count} task
          {grid.undated_count === 1 ? "" : "s"} with no date to plan from —
          in the <strong>Unscheduled</strong> column. Open it to give them a
          month.
        </p>
      ) : null}
    </div>
  );
}
